import { Axios } from 'axios';
import {
  ClusterCreateDto,
  IClusterCreateResDto,
  IGetKuberVersionDto, IGetMasterNodeConfigDto,
} from 'components/Kubernetes/types/cluster';
import {
  ICheckClusterHasLoadBalancer, ICheckIpAddressDto,
  IDisableAutoscaleReqDto, IEnableAutoscaleReqDto,
  IGetClusterByIdDto, IGetClustersDto,
  IGetMaxNumOfReplicasInCluster,
  IUpdateAutoscaleReqDto,
  IUpdateMasterNodeConfigReqDto,
} from 'components/Kubernetes/types/dtos';
import { INodePoolIncreaseForm, INodePoolsDto, NodePoolCreateDto } from 'components/Kubernetes/types/kuber-node';
import { IWorkerNodesDto } from 'components/Kubernetes/types/worker-node';
import apiClient from 'services/api/axios';
import { IReservedPublicIpsResponse } from 'store/network/types/api-bodies.types';

class KubernetesService {
  api: Axios;

  constructor(api: Axios) {
    this.api = api;
  }

  getClusters() {
    return this.api.get<IGetClustersDto>('kubernetes/getClusters');
  }

  getClusterById(clusterId: number | string) {
    return this.api.get<IGetClusterByIdDto>(`kubernetes/getCluster/${clusterId}`);
  }

  // keepPublicIp — оставить публичный IP за собой; не передан — бэкенд закрепление не трогает
  deleteCluster(clusterId: number, keepPublicIp?: boolean) {
    return this.api.post(`kubernetes/deleteCluster/${clusterId}`, null, { params: { keepPublicIp } });
  }

  getReservedPublicIps() {
    return this.api.get<IReservedPublicIpsResponse>('kubernetes/reservedPublicIps');
  }

  getMasterNodeConfig(isHa: boolean) {
    return this.api.get<IGetMasterNodeConfigDto>(`kubernetes/getMasterNodeConfig/${isHa}`);
  }

  updateMasterNodeConfig(dto: IUpdateMasterNodeConfigReqDto) {
    return this.api.patch('kubernetes/updateMasterNodeConfig', dto);
  }

  getKuberVersionsDto() {
    return this.api.get<IGetKuberVersionDto>('kubernetes/getKuberVersions');
  }

  getNodePoolsByCluster(clusterId: number | string) {
    return this.api.get<INodePoolsDto>(`kubernetes/getNodePoolsByClusters/${clusterId}`);
  }

  getWorkerNodesByPool(nodePoolId: number | string) {
    return this.api.get<IWorkerNodesDto>(`kubernetes/getK8SNodePool/${nodePoolId}`);
  }

  createCluster(dto: ClusterCreateDto) {
    return this.api.post<IClusterCreateResDto>('kubernetes/createCluster', dto);
  }

  createPool(dto: NodePoolCreateDto) {
    return this.api.post('kubernetes/createNewNodePool', dto);
  }

  increasePool(dto: INodePoolIncreaseForm) {
    return this.api.post('kubernetes/changeNodePoolSize', dto);
  }

  deletePool(poolId: number | string) {
    return this.api.post(`kubernetes/deleteNodePool/${poolId}`);
  }

  checkNetIpForAvailability(netId: number, ip: string) {
    return this.api.get<ICheckIpAddressDto>(`panel/checkNetIpForAvailability/${netId}`, {
      params: {
        ipCidr: ip,
      },
    });
  }

  checkCidrForAvailabilityInNet(netId: number, ip: string, isGateway?: boolean) {
    return this.api.get<ICheckIpAddressDto>(`panel/checkCidrForAvailabilityInNet/${netId}`, {
      params: {
        ipCidr: ip,
        isGateway: isGateway ? 'true' : undefined,
      },
    });
  }

  updateKuberVersion(clusterId: number, version: string) {
    return this.api.post(`kubernetes/updateClusterKuberVersion/${clusterId}`, undefined, {
      params: {
        version,
      },
    });
  }

  checkClusterHasLoadBalancer(clusterId: number) {
    return this.api.get<ICheckClusterHasLoadBalancer>(`kubernetes/checkClusterForLb/${clusterId}`);
  }

  getMaxNumOfReplicasInCluster() {
    return this.api.get<IGetMaxNumOfReplicasInCluster>('panel/getMaxNumOfReplicasInCluster');
  }

  enableAutoscale(dto: IEnableAutoscaleReqDto) {
    return this.api.post('kubernetes/enableAutoscaling', dto);
  }

  updateAutoscale(dto: IUpdateAutoscaleReqDto) {
    return this.api.post('kubernetes/updateAutoscaling', dto);
  }

  disableAutoscale(dto: IDisableAutoscaleReqDto) {
    return this.api.post('kubernetes/disableAutoscaler', dto);
  }
}

export default new KubernetesService(apiClient);
